<template>
  <section :class="{'inline-block': !openWithButton}">
    <button-menu
      v-if="!openWithButton"
      :text-label="$t('project_report')"
      :icon="'book'"
      :active-tooltip="true"
      :positionTooltip="'is-top'"
      :redirect="null"
      :handle-on-click="() => { isModalActive = true }"
    />

    <div v-if="openWithButton" class="mr-2 rounded-sm" @click="isModalActive = true">
      <v-button icon="book" class="mr-4">{{ $t('project_report') }}</v-button>
    </div>

    <b-modal :active.sync="isModalActive" has-modal-card>
      <div class="w-3/4 modal-card" style="max-width: 80vw;">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('generate_report') }}</p>
          <v-button @click="selectAll" class="mr-4">{{ $t('select_all') }}</v-button>
        </header>

        <div class="modal-card-filters">
          <b-field :label="$t('comments')">
            <b-switch v-model="withComments" size="is-small">{{ $t('include') }}</b-switch>
          </b-field>
          <b-field :label="$t('documents')">
            <b-switch v-model="withDocuments" size="is-small">{{ $t('include') }}</b-switch>
          </b-field>
          <b-field :label="$t('subactivities')">
            <b-switch v-model="withSubactivities" size="is-small">{{ $t('include') }}</b-switch>
          </b-field>
          <b-field :label="$t('issues')">
            <b-switch v-model="withIssues" size="is-small">{{ $t('include') }}</b-switch>
          </b-field>
          <b-field :label="$t('assignments')">
            <b-switch v-model="withAssignments" size="is-small">{{ $t('include') }}</b-switch>
          </b-field>
        </div>

        <section class="modal-card-body">
          <b-field :label="$t('activities_page_title')">
            <treeselect
              v-model="selectedOptions"
              :options="treeOptions"
              multiple
              :flat="false"
              :default-expand-level="0"
              expand-on-click-node
              placeholder="Selecciona actividad o subactividad…"
              append-to-body
              :menu-style="{ maxHeight: '50vh', overflowY: 'auto', width: '100%' }"
              @input="onSelectChange"
            />
          </b-field>
        </section>

        <footer class="modal-card-foot">
          <button class="button is-success" :disabled="isLoading" @click="generateReport">
            {{ btnGenerateReport }}
          </button>
          <button class="button" type="button" @click="isModalActive = false">
            {{ $t('close') }}
          </button>
        </footer>
      </div>
      <b-loading :active.sync="isLoading" :is-full-page="false" />
    </b-modal>
  </section>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import ButtonMenu from '../components/quick-button-menu/components/button-menu.vue'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  components: { Treeselect, ButtonMenu },

  props: {
    filters: { type: Object, required: true },
    openWithButton: { type: Boolean, default: true }
  },

  data() {
    return {
      btnGenerateReport: this.$t('generate_report'),
      isModalActive: false,
      selectedOptions: [],
      list: [],
      withComments: true,
      withDocuments: false,
      withSubactivities: true,
      withIssues: true,
      withAssignments: false,
      isLoading: false
    }
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
      project: 'app/project',
      company: 'app/company'
    }),
    treeOptions() {
      return this.list.map(act => ({
        id: act.id,
        label: act.name,
        children: (act.subactivities || []).map(sub => ({ id: sub.id, label: sub.name }))
      }))
    }
  },

  watch: {
    isModalActive(val) {
      if (val) this.loadActivitiesReport()
    }
  },

  methods: {

    async loadActivitiesReport() {
      const params = { ...this.filters }
      delete params.per_page;
      delete params.page;
      delete params.s;
      delete params.s_num;
      params.pg = 1

      const { data } = await axios.get('/api/v2/activities/report', { params })
      this.list = data.activities || []
    },

    selectAll() {
      this.selectedOptions = this.list.map(a => a.id)
    },

    onSelectChange(ids) {
      const filtered = []

      ids.forEach(id => {
        // 1) Si es padre directo, lo guardo
        if (this.list.some(a => a.id === id)) {
          filtered.push(id)
        } else {
          const parent = this.list.find(a =>
            (a.subactivities || []).some(s => s.id === id)
          )
          if (parent && !ids.includes(parent.id)) {
            filtered.push(id)
          }
        }
      })

      // Actualizo sólo si cambió
      if (filtered.sort().toString() !== ids.sort().toString()) {
        this.selectedOptions = filtered
      }
    },

    async generateReport() {
      if (!this.selectedOptions.length) {
        this.$notify.error('error_select_activity');
        return;
      }

      // Verificar si todas las opciones seleccionadas son subactividades
      const onlySubactivities = this.selectedOptions.every(id =>
        this.list.some(activity =>
          (activity.subactivities || []).some(sub => sub.id === id)
        )
      );

      // Ajustar el valor de withSubactivities
      const withSubactivities = onlySubactivities ? false : this.withSubactivities;
      this.isLoading = true;
      this.btnGenerateReport = this.$t('generating');
      this.isModalActive = false;

      const params = {
        comments: this.withComments,
        documents: this.withDocuments,
        subactivities: withSubactivities, // Enviar el valor ajustado
        issues: this.withIssues,
        assignments: this.withAssignments,
        activities: this.selectedOptions
      };

      // console.log('params', params);

      const { data } = await axios.get(
        `/api/v2/report/generate/${this.company.alias}`,
        { params }
      );

      this.$notify[data ? 'success' : 'error'](data ? 'generating_report' : 'error_loading_report');
      this.isLoading = false;
      this.btnGenerateReport = this.$t('generate_report');
    }
  }
}
</script>

<style scoped lang="scss">
.modal-card-filters {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}
.modal-card-body {
  overflow-y: auto;
  max-height: 60vh;
  padding: 1rem;
}
:deep(.vue-treeselect__menu) {
  z-index: 9999 !important;
  max-height: 70vh !important;
}
</style>
